import { Loader } from '@10xLabs/react-web/components/Loader'
import { useMutation, NetworkStatus, useQuery } from '@apollo/client'
import { ReactElement, useEffect } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'

import FormLayout from 'components/v2/FormLayout'
import Heading from 'components/v2/Heading'
import { ForbiddenView } from 'features/App/components/ForbiddenView'
import { withLastUrl } from 'features/App/components/WithLastUrl'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { canWrite } from 'features/Users/utils'
import { useLogin } from 'features/Login/hooks'
import { useNavigate, useParams } from 'react-router-dom'
import EmptyState from 'components/v2/EmptyState'
import { Button } from 'components/v2/Button'
import { INewBusinessClientUserFormValues } from 'features/BusinessClients/types'
import { NewBusinessClientUser } from 'features/BusinessClients/components/NewBusinessClientUser'
import {
  CHANGE_BUSINESS_CLIENT_USER,
  CHANGE_BUSINESS_CLIENT_USER_BUSINESS_CLIENT
} from 'features/BusinessClients/mutations'
import { CHANGE_USER_ACCOUNT_DETAILS } from 'features/BusinessCustomer/mutations-legacy'
import { GET_BUSINESS_CLIENT_USER } from 'features/BusinessClientUsers/queries'
import { businessClientUserFormUtils } from 'features/BusinessClients/utils'
import { BusinessClientUser } from 'gql/generated/graphql'
import { isEmpty } from 'lodash'

const messages = defineMessages({
  helmet: {
    id: 'companyClients.edit.helmet',
    defaultMessage: 'Edit Company Client | Nexbus Admin'
  }
})

const EditBusinessClientUserView = (): ReactElement => {
  const intl = useIntl()
  const { businessClientId, businessClientUserId } = useParams()
  const navigate = useNavigate()
  const login = useLogin()
  let content: ReactElement

  const [
    changeBusinessClientUserBusinessClient,
    { loading: changeBusinessClientUserBusinessClientLoading, error: changeBusinessClientUserBusinessClientError }
  ] = useMutation(CHANGE_BUSINESS_CLIENT_USER_BUSINESS_CLIENT)
  const [changeUserAccountDetails, { loading: changeUserAccountDetailsLoading, error: changeUserAccountDetailsError }] =
    useMutation(CHANGE_USER_ACCOUNT_DETAILS)
  const [changeBusinessClientUser, { loading: changeBusinessClientUserLoading, error: changeBusinessClientUserError }] =
    useMutation(CHANGE_BUSINESS_CLIENT_USER)

  const {
    loading: queryLoading,
    error: queryError,
    data: queryData,
    refetch,
    networkStatus
  } = useQuery(GET_BUSINESS_CLIENT_USER, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    variables: {
      id: businessClientUserId
    }
  })

  const handleSubmit = async (formValues: INewBusinessClientUserFormValues): Promise<void> => {
    const mutationsInput = businessClientUserFormUtils.mapFormToUpdateMutation(
      formValues,
      queryData?.businessClientUser as BusinessClientUser
    )

    try {
      if (!isEmpty(mutationsInput.businessClientUserBusinessClientInput)) {
        await changeBusinessClientUserBusinessClient({
          variables: {
            input: mutationsInput.businessClientUserBusinessClientInput
          }
        })
      }
      if (!isEmpty(mutationsInput.userAccountInput)) {
        await changeUserAccountDetails({
          variables: {
            input: mutationsInput.userAccountInput
          }
        })
      }
      if (!isEmpty(mutationsInput.businessClientUserInput)) {
        await changeBusinessClientUser({
          variables: {
            input: mutationsInput.businessClientUserInput
          }
        })
      }
      navigate(`/business-clients/${formValues.businessClient?.id}/users`)
    } catch (ex) {
      // do nothing
    }
  }

  useEffect(() => {
    document.title = intl.formatMessage(messages.helmet)
  }, [])

  if (!canWrite(FEATURES.BUSINESS_CLIENT_USER, login.accessToken.policies)) {
    return <ForbiddenView feature={FEATURES.BUSINESS_CLIENT_USER} />
  }

  if (networkStatus === NetworkStatus.refetch || queryLoading) {
    content = (
      <FormLayout.Content>
        <FormLayout.Card>
          <EmptyState.Container>
            <Loader />
          </EmptyState.Container>
        </FormLayout.Card>
      </FormLayout.Content>
    )
  } else if (queryError) {
    content = (
      <FormLayout.Content>
        <FormLayout.Card>
          <EmptyState.Container>
            <EmptyState.Description>
              <FormattedMessage
                id="businessClientUsers.edit.error.description"
                defaultMessage="There was an error while loading the user"
              />
            </EmptyState.Description>
            <EmptyState.Action>
              <Button
                onClick={(): void => {
                  refetch()
                }}
                kind="primary"
              >
                <FormattedMessage id="businessClientUsers.edit.error.action" defaultMessage="Reload the user" />
              </Button>
            </EmptyState.Action>
          </EmptyState.Container>
        </FormLayout.Card>
      </FormLayout.Content>
    )
  } else {
    const loading =
      changeUserAccountDetailsLoading ||
      changeBusinessClientUserLoading ||
      changeBusinessClientUserBusinessClientLoading
    const error =
      changeUserAccountDetailsError || changeBusinessClientUserError || changeBusinessClientUserBusinessClientError
    content = (
      <FormLayout.Content>
        <Heading.H3>
          <FormattedMessage id="businessClientUsers.edit.title" defaultMessage="Edit business client user" />
        </Heading.H3>
        <NewBusinessClientUser
          isEdit
          onSubmit={handleSubmit}
          loading={loading}
          error={error}
          businessClientId={businessClientId as string}
          businessClientUser={queryData?.businessClientUser}
        />
      </FormLayout.Content>
    )
  }
  return <FormLayout.Container>{content}</FormLayout.Container>
}

export default withLastUrl(EditBusinessClientUserView)
